import { VertesiaClient } from "@vertesia/client"
import { AgentMessageType, ContentObjectItem } from "@vertesia/common"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"

import { AgentMessage } from "@/models/agent"

dayjs.extend(relativeTime)

export function insertMessageInTimeline(arr: AgentMessage[], m: AgentMessage) {
    const t = typeof m.timestamp === "number" ? m.timestamp : new Date(m.timestamp).getTime()
    const idx = arr.findIndex((a) => {
        const at = typeof a.timestamp === "number" ? a.timestamp : new Date(a.timestamp).getTime()
        return at > t
    })
    if (idx === -1) {
        arr.push(m)
    } else {
        arr.splice(idx, 0, m)
    }
}

export function isInProgress(messages: AgentMessage[]) {
    if (!messages.length) {
        return true
    }

    const workstreamMessages = new Map<string, AgentMessage[]>()

    messages.forEach((message) => {
        const workstreamId = getWorkstreamId(message)
        if (!workstreamMessages.has(workstreamId)) {
            workstreamMessages.set(workstreamId, [])
        }
        workstreamMessages.get(workstreamId)!.push(message)
    })

    if (workstreamMessages.size === 1 && !workstreamMessages.has("main")) {
        const onlyWorkstreamId = workstreamMessages.keys().next().value || "unknown"

        const onlyWorkstreamMsgs = workstreamMessages.get(onlyWorkstreamId)!
        const lastMessage = onlyWorkstreamMsgs[onlyWorkstreamMsgs.length - 1]

        return ![AgentMessageType.COMPLETE, AgentMessageType.IDLE, AgentMessageType.REQUEST_INPUT].includes(lastMessage.type)
    }

    if (workstreamMessages.has("main")) {

        const mainWorkstreamMsgs = workstreamMessages.get("main")!

        if (mainWorkstreamMsgs.length === 0) {
            return true
        }

        const lastMainMessage = mainWorkstreamMsgs[mainWorkstreamMsgs.length - 1]
        return [AgentMessageType.COMPLETE, AgentMessageType.IDLE, AgentMessageType.REQUEST_INPUT].includes(lastMainMessage.type)
    }

    for (const [workstreamId, msgs] of workstreamMessages.entries()) {
        if (msgs.length > 0) {
            const lastMessage = msgs[msgs.length - 1];
            if ([AgentMessageType.COMPLETE, AgentMessageType.IDLE, AgentMessageType.REQUEST_INPUT].includes(lastMessage.type)) {
                return true
            }
        }
    }

    return false
}

export const formatRelative = (ts: number | string) => typeof ts === "number" ? dayjs(ts).fromNow() : dayjs(new Date(ts)).fromNow()

export async function getConversationUrl(vertesia: VertesiaClient, workflowRunId: string): Promise<string> {
    return vertesia.files.getDownloadUrl(`agents/conversation-${workflowRunId}.json`).then((r) => r.url);
}

export function getWorkstreamId(message: AgentMessage): string {
    if (message.workstream_id) {
        return message.workstream_id
    }

    return "main"
}

export function getWorkstreamStatusMap(messages: AgentMessage[]): Map<string, "pending" | "in_progress" | "completed"> {
    const workstreamMessages = new Map<string, AgentMessage[]>()
    const statusMap = new Map<string, "pending" | "in_progress" | "completed">()

    messages.forEach((message) => {
        const workstreamId = getWorkstreamId(message)
        if (!workstreamMessages.has(workstreamId)) {
            workstreamMessages.set(workstreamId, [])
            statusMap.set(workstreamId, "pending")
        }
        workstreamMessages.get(workstreamId)!.push(message)
    })

    for (const [workstreamId, msgs] of workstreamMessages.entries()) {
        if (msgs.length > 0) {
            statusMap.set(workstreamId, "in_progress")

            const lastMessage = msgs[msgs.length - 1]

            const hasCompleteMessage = msgs.some(msg =>
                msg.type === AgentMessageType.COMPLETE &&
                (msg.workstream_id === workstreamId || (!msg.workstream_id && workstreamId === "main"))
            )

            if (hasCompleteMessage || [AgentMessageType.COMPLETE, AgentMessageType.IDLE, AgentMessageType.REQUEST_INPUT].includes(lastMessage.type)) {
                statusMap.set(workstreamId, "completed")
            }
        }
    }

    return statusMap
}

export function generateInputMessage(input: string, files: ContentObjectItem[]) {
    if (!files.length) {
        return input
    }

    for (const file of files) {
        const title = file.properties?.title || file.name || "Object"
        input += `\n\n[${title}](store:${file.id})`
    }

    return input
}